import { useCollapsedState } from "@lib/hooks/useCollapsedState";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useId } from "react";

interface CollapsibleSectionProps {
  title: string;
  /** Persist the expanded state under this localStorage key; omit for session-only state. */
  storageKey?: string;
  defaultExpanded?: boolean;
  /** Controlled mode: when set, the parent owns the state and receives `onToggle`. */
  expanded?: boolean;
  onToggle?: () => void;
  children: React.ReactNode;
  className?: string;
}

export function CollapsibleSection({
  title,
  storageKey,
  defaultExpanded = true,
  expanded,
  onToggle,
  children,
  className = "",
}: CollapsibleSectionProps) {
  const [storedExpanded, toggleStored] = useCollapsedState(storageKey, defaultExpanded);
  const isExpanded = expanded ?? storedExpanded;
  const toggle = onToggle ?? toggleStored;
  const contentId = useId();

  return (
    <div className={className}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={isExpanded}
        aria-controls={contentId}
        className="flex w-full items-center gap-2 py-2 text-left"
      >
        {isExpanded ? (
          <ChevronDown size={16} className="text-text-muted" />
        ) : (
          <ChevronRight size={16} className="text-text-muted" />
        )}
        <h3 className="text-sm font-medium text-text-secondary">{title}</h3>
      </button>
      <div
        id={contentId}
        className="grid transition-[grid-template-rows] duration-200 ease-in-out"
        style={{ gridTemplateRows: isExpanded ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">{children}</div>
      </div>
    </div>
  );
}
